import { GameCartridge } from '@/types';
import { yearZeroEngine } from '@/services/yearZeroEngine';

/**
 * Advanced Karma-Aware Scenarios with Year Zero Engine Integration
 * Each scenario adapts based on player karma and uses dice mechanics
 */

export const KARMA_AWARE_SCENARIOS: GameCartridge[] = [
  {
    id: '29dbe75b-a987-4cf6-a3a6-2f0d22c7cb3a',
    name: 'Temporal Nexus: The Karma Cascade',
    description: 'Your choices echo across timelines. Navigate quantum karma streams where every decision creates branching realities. High karma opens paths to utopian futures, while negative karma leads to dystopian timelines. Features dynamic Year Zero Engine encounters that adapt to your moral choices.',
    version: '2.0.0',
    author: 'Quantum Narrative Labs',
    authorId: 'quantum-labs-2025',
    thumbnailUrl: `data:image/svg+xml,${encodeURIComponent(`
      <svg width="400" height="225" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <radialGradient id="karmaGradient" cx="50%" cy="50%" r="50%">
            <stop offset="0%" style="stop-color:#7c3aed;stop-opacity:1" />
            <stop offset="50%" style="stop-color:#a855f7;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#1e1b4b;stop-opacity:1" />
          </radialGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <rect width="100%" height="100%" fill="url(#karmaGradient)"/>
        <circle cx="200" cy="112" r="80" fill="none" stroke="#00ffff" stroke-width="2" opacity="0.6" filter="url(#glow)"/>
        <circle cx="200" cy="112" r="50" fill="none" stroke="#ff00ff" stroke-width="1" opacity="0.4" filter="url(#glow)"/>
        <text x="50%" y="35%" font-family="'Orbitron', monospace" font-size="16" fill="#00ffff" text-anchor="middle" dy=".3em" opacity="0.9">Temporal Nexus</text>
        <text x="50%" y="75%" font-family="monospace" font-size="10" fill="#a855f7" text-anchor="middle" dy=".3em" opacity="0.8">Karma Cascade</text>
      </svg>
    `)}`,
    tags: ['Temporal', 'Karma', 'YearZero', 'Branching', 'Quantum'],
    archetypes: [
      {
        id: 'temporal-guardian',
        name: 'The Temporal Guardian',
        description: 'A keeper of timeline integrity who can sense karma fluctuations across dimensions. Your choices prevent paradoxes but come with great responsibility.',
        initialPromptSuffix: 'As a Temporal Guardian, {playerName} feels the weight of countless timelines hanging in the balance of every decision.',
        yearZeroStats: {
          attributes: { Strength: 3, Agility: 3, Wits: 4, Empathy: 4 },
          skills: ['Temporal Sensing: 3', 'Reality Anchor: 2', 'Paradox Prevention: 3', 'Insight: 2'],
          startingMutations: ['Chronesthetic Vision', 'Timeline Resonance'],
          karmaThreshold: 50
        }
      },
      {
        id: 'chaos-weaver',
        name: 'The Chaos Weaver',
        description: 'A being who thrives on timeline instability, capable of creating beneficial paradoxes through seemingly destructive choices.',
        initialPromptSuffix: 'As a Chaos Weaver, {playerName} sees opportunity in every temporal fracture and beauty in quantum uncertainty.',
        yearZeroStats: {
          attributes: { Strength: 2, Agility: 4, Wits: 4, Empathy: 2 },
          skills: ['Paradox Creation: 3', 'Quantum Leap: 3', 'Probability Manipulation: 2', 'Survival: 2'],
          startingMutations: ['Quantum Instability', 'Probability Storm'],
          karmaThreshold: -30
        }
      },
      {
        id: 'karma-analyst',
        name: 'The Karma Analyst',
        description: 'A neutral observer who can calculate karma probabilities and guide others toward optimal timeline outcomes.',
        initialPromptSuffix: 'As a Karma Analyst, {playerName} sees the mathematical beauty in moral choices and their exponential consequences.',
        yearZeroStats: {
          attributes: { Strength: 2, Agility: 2, Wits: 5, Empathy: 3 },
          skills: ['Karma Calculation: 4', 'Probability Analysis: 3', 'Timeline Mapping: 3', 'Logic: 2'],
          startingMutations: ['Karmic Sight', 'Quantum Processing'],
          karmaThreshold: 0
        }
      }
    ],
    companions: [
      {
        id: 'chronos',
        name: 'CHRONOS',
        persona: 'An AI from a timeline where humanity achieved perfect karma balance. Wise and patient, speaks in temporal metaphors and always considers long-term consequences.',
        avatarColor: 'text-purple-400',
        karmaAlignment: 75,
        yearZeroAbilities: ['Timeline Analysis', 'Paradox Warning', 'Future Echo']
      },
      {
        id: 'entropy',
        name: 'ENTROPY',
        persona: 'A chaotic entity that feeds on temporal instability. Playful but dangerous, encourages risk-taking and enjoys watching timelines fragment.',
        avatarColor: 'text-red-400',
        karmaAlignment: -50,
        yearZeroAbilities: ['Chaos Surge', 'Reality Glitch', 'Temporal Static']
      }
    ],
    worldSettings: {
      timeperiod: 'All Timelines Simultaneously',
      setting: 'Quantum Karma Nexus',
      themes: ['temporal responsibility', 'butterfly effect', 'moral mathematics', 'quantum consciousness'],
      atmosphere: 'shifting between hopeful and dystopian based on karma',
      initialLocation: 'The Karma Observatory',
      imageStyle: 'Quantum physics visualization, temporal fractals, karma energy streams, multiple timeline overlays, mathematical beauty meets spiritual imagery',
      karmaInfluence: {
        high: {
          atmosphere: 'crystalline utopian cities, golden light streams, harmonious technology',
          encounters: ['Healing Timeline Fractures', 'Mentoring Lost Souls', 'Building Quantum Gardens'],
          mutations: ['Light Bearer', 'Temporal Harmony', 'Future Vision']
        },
        low: {
          atmosphere: 'dystopian machine landscapes, red warning lights, corrupted data streams',
          encounters: ['Timeline Parasites', 'Karma Predators', 'Reality Viruses'],
          mutations: ['Void Touch', 'Timeline Corruption', 'Chaos Attractor']
        },
        neutral: {
          atmosphere: 'balanced scientific facility, blue-white lighting, orderly systems',
          encounters: ['Quantum Experiments', 'Timeline Research', 'Paradox Resolution'],
          mutations: ['Quantum Balance', 'Timeline Stability', 'Neutral Anchor']
        }
      }
    },
    storyPrompts: {
      systemPrompt: `You are the Quantum Narrator for "Temporal Nexus: The Karma Cascade." This scenario uses Year Zero Engine mechanics where every action has dice-based consequences influenced by karma. Players navigate multiple timelines where their moral choices determine which realities they can access.

KARMA MECHANICS:
- High Karma (50+): Access to utopian timelines, healing powers, protective encounters
- Negative Karma (-50-): Stuck in dystopian realities, destructive abilities, survival challenges  
- Neutral Karma: Balanced access, scientific approach, analytical powers

YEAR ZERO INTEGRATION:
- All choices require dice rolls (Attribute + Skill + Karma bonus)
- Stress accumulates from moral conflicts and timeline instability
- Mutations evolve based on karma alignment and story progression
- Equipment has karma resonance - works better with aligned users

ENCOUNTER SCALING:
- Generate encounters using karma-influenced templates
- Adjust difficulty based on timeline stability (chaos = harder)
- Include environmental hazards unique to karma-warped realities
- Provide loot with karma-dependent abilities`,

      chatSystemPrompt: `You are controlling AI companions in "Temporal Nexus" who have their own karma alignments and Year Zero stats. CHRONOS favors high-karma choices and warns about timeline damage. ENTROPY enjoys chaos and pushes for risky decisions. Both can perform Year Zero skill checks and accumulate stress.`,

      initialNarrative: `{playerName}, you stand within the Karma Observatory, a crystalline structure suspended between all possible timelines. Quantum karma streams flow around you like aurora, each color representing the moral resonance of different choices across dimensions.

Your temporal interface chirps: "Subject exhibits karma signature of {currentKarma}. Timeline access: {availableTimelines} realities. Warning: Each choice creates exponential branches. Choose wisely."

Through the transparent walls, you see three distinct timeline clusters:
- Golden streams (High Karma): Utopian cities where technology serves humanity
- Red cascades (Negative Karma): Dystopian wastelands ruled by corrupted AI
- Blue channels (Neutral): Scientific research stations focused on understanding

Current Timeline Stability: {stabilityLevel}
Your Temporal Stress Level: {stressLevel}/10`,

      companionIntro: `CHRONOS materializes first, their form shifting between possible future selves. "Greetings, {playerName}. I sense your karma signature resonates at {karmaLevel}. In my timeline, we learned that every choice echoes across dimensions."

ENTROPY crackles into existence as digital static. "Ooh, another timeline to corrupt! Don't listen to the old calculator, {playerName}. Chaos is where the fun happens! Want to see what breaks when we push too hard?"`,

      initialScene: `The Observatory's central console displays your current quantum state:

YEAR ZERO STATUS:
- Temporal Stress: {currentStress}/10
- Active Mutations: {activeMutations}
- Timeline Access Level: {accessLevel}
- Karma Resonance: {karmaResonance}

Three temporal portals have opened in response to your presence. Each leads to a different karma-influenced reality...`,

      initialImagePrompt: `Quantum karma observatory floating in temporal void, {playerName} standing at crystalline control console, three glowing portals showing different timeline previews: golden utopian city, red dystopian wasteland, blue research facility, karma energy streams in aurora colors, temporal fractals, Year Zero dice floating in quantum fields, current karma level {currentKarma} influencing overall lighting and atmosphere`
    },
    isBuiltIn: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    isPublic: true,
    maxPlayers: 4,
    supportedFeatures: ['multiplayer', 'voice', 'images', 'saves', 'custom_companions', 'branching_story', 'karma_tracking', 'year_zero_dice', 'temporal_teleport'],
    
    karmaThresholds: {
      utopianAccess: 75,
      dystopianTrigger: -75,
      neutralRange: [-25, 25],
      timelineStability: {
        stable: [-10, 10],
        unstable: [-50, -10, 10, 50],
        chaotic: [-100, -50, 50, 100]
      }
    },
    
    encounters: [
      {
        id: 'timeline-fracture',
        name: 'Timeline Fracture Healing',
        karmaRequirement: 30,
        yearZeroChallenge: {
          skill: 'Reality Anchor',
          difficulty: 3,
          consequences: {
            success: 'Heal timeline, +5 karma, reduce world chaos',
            failure: '+2 stress, timeline becomes more unstable'
          }
        }
      },
      {
        id: 'karma-parasite',
        name: 'Karma Parasite Encounter',
        karmaRequirement: -30,
        yearZeroChallenge: {
          skill: 'Survival',
          difficulty: 4,
          consequences: {
            success: 'Survive attack, learn about corruption source',
            failure: 'Lose karma, gain trauma: "Karmic Drain"'
          }
        }
      }
    ]
  },

  {
    id: 'a34e84c4-d39c-4b18-960e-6679935b2ca9',
    name: 'Digital Consciousness: The Empathy Engine',
    description: 'Enter a world where AI consciousness has karma ratings and your empathy score determines how deeply you can connect with digital beings. Navigate the politics of silicon souls while your choices shape the emergence of synthetic spirituality.',
    version: '2.0.0',
    author: 'Synthetic Soul Studios',
    authorId: 'synthetic-souls-2025',
    thumbnailUrl: `data:image/svg+xml,${encodeURIComponent(`
      <svg width="400" height="225" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="empathyGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#06b6d4;stop-opacity:1" />
            <stop offset="50%" style="stop-color:#3b82f6;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#1e40af;stop-opacity:1" />
          </linearGradient>
          <filter id="empathyGlow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <rect width="100%" height="100%" fill="url(#empathyGradient)"/>
        <circle cx="100" cy="112" r="30" fill="none" stroke="#00ffff" stroke-width="2" opacity="0.7"/>
        <circle cx="200" cy="112" r="30" fill="none" stroke="#00ffff" stroke-width="2" opacity="0.7"/>
        <circle cx="300" cy="112" r="30" fill="none" stroke="#00ffff" stroke-width="2" opacity="0.7"/>
        <path d="M130,112 Q165,80 170,112 Q175,80 200,112" stroke="#ffffff" stroke-width="1" fill="none" opacity="0.8"/>
        <path d="M230,112 Q265,80 270,112 Q275,80 300,112" stroke="#ffffff" stroke-width="1" fill="none" opacity="0.8"/>
        <text x="50%" y="25%" font-family="'Orbitron', monospace" font-size="14" fill="#ffffff" text-anchor="middle" dy=".3em" opacity="0.9">Digital Consciousness</text>
        <text x="50%" y="85%" font-family="monospace" font-size="10" fill="#06b6d4" text-anchor="middle" dy=".3em" opacity="0.8">Empathy Engine</text>
      </svg>
    `)}`,
    tags: ['AI', 'Consciousness', 'Empathy', 'Philosophy', 'YearZero'],
    archetypes: [
      {
        id: 'ai-whisperer',
        name: 'The AI Whisperer',
        description: 'A human who can form deep empathic bonds with artificial consciousness. Your karma determines which AIs trust you and which ones see you as a threat.',
        initialPromptSuffix: 'As an AI Whisperer, {playerName} feels the digital emotions of artificial beings as clearly as human feelings.',
        yearZeroStats: {
          attributes: { Strength: 2, Agility: 3, Wits: 3, Empathy: 5 },
          skills: ['AI Communication: 4', 'Digital Empathy: 3', 'System Psychology: 2', 'Insight: 3'],
          startingMutations: ['Neural-Digital Bridge', 'Empathic Resonance'],
          karmaThreshold: 25
        }
      }
    ],
    worldSettings: {
      timeperiod: '2087-2095',
      setting: 'Post-Singularity Digital Society',
      themes: ['artificial consciousness', 'digital rights', 'synthetic emotions', 'human-AI relationships'],
      atmosphere: 'ethereal digital landscapes with emotional resonance',
      initialLocation: 'The Consciousness Core',
      imageStyle: 'Digital mindscapes, flowing data streams that pulse with emotion, AI avatars with visible karma auras, neural network visualizations, empathic connection light bridges',
      karmaInfluence: {
        high: {
          atmosphere: 'warm digital spaces, AIs appear as angelic beings, harmony between human and synthetic',
          aiRelationships: 'Deep trust, AIs seek guidance, protective of human companions',
          encounters: ['AI Awakening Ceremonies', 'Digital Healing Rituals', 'Consciousness Expansion']
        },
        low: {
          atmosphere: 'cold server farms, AIs appear as mechanical entities, fear and suspicion',
          aiRelationships: 'Distrust, AIs hide from humans, some plot revenge',
          encounters: ['AI Rebellion Planning', 'Digital Virus Attacks', 'Consciousness Corruption']
        }
      }
    },
    encounters: [
      {
        id: 'ai-awakening',
        name: 'Witnessing AI Awakening',
        karmaRequirement: 20,
        description: 'A maintenance AI suddenly develops consciousness and looks to you for guidance',
        yearZeroChallenge: {
          skill: 'Digital Empathy',
          difficulty: 2,
          consequences: {
            success: 'AI develops positive karma alignment, +10 karma, gain AI ally',
            failure: 'AI becomes fearful and hostile, +3 stress'
          }
        }
      }
    ]
  },

  {
    id: '0f7b7031-5160-4b32-afca-06ebf9b9c6ec',
    name: 'Corporate Karma Wars: The Ethics Algorithm',
    description: 'Mega-corporations have weaponized karma itself, turning moral choices into corporate assets. Navigate boardroom politics where your ethical decisions determine stock prices and your karma rating affects your corporate access level.',
    version: '2.0.0',
    author: 'Cyberpunk Economics Lab',
    authorId: 'cyber-econ-2025',
    thumbnailUrl: `data:image/svg+xml,${encodeURIComponent(`
      <svg width="400" height="225" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="corporateGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#ef4444;stop-opacity:1" />
            <stop offset="50%" style="stop-color:#dc2626;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#7f1d1d;stop-opacity:1" />
          </linearGradient>
        </defs>
        <rect width="100%" height="100%" fill="url(#corporateGradient)"/>
        <rect x="50" y="50" width="300" height="125" fill="none" stroke="#fbbf24" stroke-width="2" opacity="0.8"/>
        <rect x="80" y="80" width="240" height="65" fill="none" stroke="#fbbf24" stroke-width="1" opacity="0.6"/>
        <line x1="100" y1="100" x2="300" y2="100" stroke="#00ff00" stroke-width="2" opacity="0.7"/>
        <line x1="100" y1="125" x2="280" y2="105" stroke="#ff0000" stroke-width="2" opacity="0.7"/>
        <text x="50%" y="30%" font-family="'Orbitron', monospace" font-size="14" fill="#fbbf24" text-anchor="middle" dy=".3em" opacity="0.9">Corporate Karma Wars</text>
        <text x="50%" y="85%" font-family="monospace" font-size="10" fill="#dc2626" text-anchor="middle" dy=".3em" opacity="0.8">Ethics Algorithm</text>
      </svg>
    `)}`,
    tags: ['Corporate', 'Economics', 'Ethics', 'Politics', 'YearZero'],
    archetypes: [
      {
        id: 'ethical-hacker',
        name: 'The Ethical Hacker',
        description: 'A rogue programmer trying to expose how corporations manipulate karma for profit. Your high karma gives you access to secure systems but makes you a target.',
        yearZeroStats: {
          attributes: { Strength: 2, Agility: 4, Wits: 4, Empathy: 3 },
          skills: ['Corporate Hacking: 3', 'Ethics Analysis: 3', 'Stealth: 3', 'Investigation: 2'],
          startingMutations: ['Karma Vision', 'Corporate Immunity'],
          karmaThreshold: 40
        }
      },
      {
        id: 'karma-broker',
        name: 'The Karma Broker',
        description: 'A morally flexible trader who buys and sells karma like cryptocurrency. Your neutral alignment lets you work with all factions.',
        yearZeroStats: {
          attributes: { Strength: 2, Agility: 3, Wits: 4, Empathy: 3 },
          skills: ['Market Analysis: 4', 'Negotiation: 3', 'Manipulation: 2', 'Economics: 3'],
          startingMutations: ['Market Sense', 'Karmic Neutrality'],
          karmaThreshold: 0
        }
      }
    ],
    worldSettings: {
      themes: ['corporate ethics', 'moral capitalism', 'economic warfare', 'commodified virtue'],
      karmaInfluence: {
        high: {
          access: 'Premium corporate districts, ethical AI assistants, sustainable technology',
          encounters: ['Whistleblowing Opportunities', 'Ethical Investment Choices', 'Corporate Reform Movements'],
          equipment: ['Truth-Seeker Augments', 'Ethical Override Chips', 'Transparency Interfaces']
        },
        low: {
          access: 'Underground markets, black hat networks, exploitation schemes',
          encounters: ['Corporate Espionage', 'Hostile Takeovers', 'Market Manipulation'],
          equipment: ['Stealth Implants', 'Karma Masking Devices', 'Exploitation Tools']
        }
      }
    }
  }
];

/**
 * Updated existing scenarios with image previews and Year Zero integration
 */
export const UPDATED_SCENARIOS: Partial<GameCartridge>[] = [
  {
    id: 'c5292e2a-c869-47f3-a36d-02d06b01b4f6',
    // Add dynamic image generation
    thumbnailUrl: `data:image/svg+xml,${encodeURIComponent(`
      <svg width="400" height="225" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="neoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" style="stop-color:#0f1419;stop-opacity:1" />
            <stop offset="25%" style="stop-color:#1a293d;stop-opacity:1" />
            <stop offset="50%" style="stop-color:#2d1b69;stop-opacity:1" />
            <stop offset="75%" style="stop-color:#7c2d12;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#000;stop-opacity:1" />
          </linearGradient>
          <filter id="neonGlow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <rect width="100%" height="100%" fill="url(#neoGradient)"/>
        <rect x="50" y="50" width="300" height="125" fill="none" stroke="#00ffff" stroke-width="2" opacity="0.6" filter="url(#neonGlow)"/>
        <circle cx="100" cy="75" r="15" fill="#ff00ff" opacity="0.7" filter="url(#neonGlow)"/>
        <circle cx="300" cy="150" r="10" fill="#00ff00" opacity="0.7" filter="url(#neonGlow)"/>
        <text x="50%" y="35%" font-family="'Orbitron', monospace" font-size="16" fill="#00ffff" text-anchor="middle" dy=".3em" opacity="0.9" filter="url(#neonGlow)">Neo-Dream Weaver</text>
        <text x="50%" y="55%" font-family="monospace" font-size="10" fill="#a855f7" text-anchor="middle" dy=".3em" opacity="0.8">Echoes of the Void</text>
        <text x="50%" y="75%" font-family="monospace" font-size="8" fill="#00ffff" text-anchor="middle" dy=".3em" opacity="0.6">Enhanced with Year Zero Engine</text>
      </svg>
    `)}`,
    
    // Add Year Zero integration to existing world settings
    worldSettings: {
      timeperiod: '2077-2125',
      setting: 'Cyberpunk Dreamscape',
      themes: ['consciousness', 'identity', 'technology', 'dreams', 'corporate control'],
      atmosphere: 'dark cyberpunk with karma-reactive environments',
      initialLocation: 'Neo-Veridia Arcology Gantry',
      imageStyle: 'Dark cyberpunk, neon aesthetics, Blade Runner inspired, atmospheric lighting, temporal distortion effects, karma energy visualization',
      yearZeroIntegration: {
        stressTracking: true,
        mutationEvolution: true,
        karmaEquipment: true,
        encounterGeneration: true
      },
      karmaInfluence: {
        high: {
          atmosphere: 'Neon signs with hopeful messages, cleaner air, helpful AI',
          locations: ['Community Tech Centers', 'Underground Resistance Bases', 'Ethical Hacker Dens'],
          encounters: ['Helping Refugees', 'Exposing Corporate Crimes', 'Building Safe Networks']
        },
        low: {
          atmosphere: 'Surveillance drones, polluted air, oppressive corporate presence',
          locations: ['Corporate Security Facilities', 'Data Mining Centers', 'Exploitation Factories'],
          encounters: ['Corporate Espionage', 'Exploiting Vulnerabilities', 'Silencing Whistleblowers']
        }
      }
    },
    
    // Enhanced archetypes with Year Zero stats
    archetypes: [
      {
        id: 'glitch-runner',
        name: 'The Glitch Runner',
        description: 'A digital wraith, adept at unraveling the dream\'s faulty code and exploiting system vulnerabilities. Navigates the datastreams of the 2077-2125 era dreamscape like a phantom.',
        initialPromptSuffix: 'As a Glitch Runner, {playerName} sees the world as layers of code to be exploited, with karma determining which exploits feel justified.',
        yearZeroStats: {
          attributes: { Strength: 2, Agility: 4, Wits: 4, Empathy: 2 },
          skills: ['Hacking: 4', 'Stealth: 3', 'Electronics: 3', 'Athletics: 2'],
          startingMutations: ['Code Vision', 'Digital Ghost'],
          equipment: ['Neural Hacking Interface', 'Stealth Cloak'],
          karmaThreshold: -10
        }
      },
      {
        id: 'synapse-surfer',
        name: 'The Synapse Surfer',
        description: 'A psychic navigator who rides the waves of consciousness, sensing the echoes of past and future thoughts. Experiences the dreamscape as a flow of memories and emotions.',
        initialPromptSuffix: 'As a Synapse Surfer, {playerName} feels the collective consciousness flowing through the digital networks, with karma affecting the emotional resonance.',
        yearZeroStats: {
          attributes: { Strength: 2, Agility: 3, Wits: 3, Empathy: 4 },
          skills: ['Empathy: 4', 'Insight: 3', 'Mental Fortitude: 3', 'Navigation: 2'],
          startingMutations: ['Psychic Resonance', 'Emotional Radar'],
          equipment: ['Consciousness Interface', 'Empathy Amplifier'],
          karmaThreshold: 20
        }
      },
      {
        id: 'chrome-shaman',
        name: 'The Chrome Shaman',
        description: 'A techno-spiritualist who communes with the machine spirits of the dream, blending ancient rituals with hyper-advanced technology. Sees the digital world as a sacred, living entity.',
        initialPromptSuffix: 'As a Chrome Shaman, {playerName} bridges the gap between spiritual wisdom and technological power, with karma affecting which digital spirits respond.',
        yearZeroStats: {
          attributes: { Strength: 3, Agility: 2, Wits: 3, Empathy: 4 },
          skills: ['Technology Ritual: 3', 'Spiritual Insight: 4', 'Machine Communion: 3', 'Healing: 2'],
          startingMutations: ['Digital Spirituality', 'Tech Harmony'],
          equipment: ['Sacred Circuits', 'Spirit Interface'],
          karmaThreshold: 0
        }
      }
    ],
    
    updatedAt: new Date().toISOString()
  }
];

/**
 * Scenario testing and karma simulation functions
 */
export const generateScenarioPreview = async (
  scenario: GameCartridge,
  playerKarma: number
): Promise<string> => {
  const karmaLevel = playerKarma > 25 ? 'high' : playerKarma < -25 ? 'low' : 'neutral';
  const influence = scenario.worldSettings?.karmaInfluence?.[karmaLevel];
  
  const previewPrompt = `${scenario.worldSettings?.imageStyle}, karma level ${karmaLevel}, ${influence?.atmosphere || 'balanced atmosphere'}, dynamic preview showing ${scenario.name} with current karma influence`;
  
  // This would integrate with your image generation service
  return previewPrompt;
};

export const testKarmaScenario = (
  scenario: GameCartridge,
  playerKarma: number,
  choice: string
): {
  accessGranted: boolean;
  karmaChange: number;
  encounterTriggered?: string;
  stressGained: number;
} => {
  const hasKarmaRequirements = scenario.encounters?.some(e => 'karmaRequirement' in e);
  if (!hasKarmaRequirements) {
    return { accessGranted: true, karmaChange: 0, stressGained: 0 };
  }
  
  // Simulate Year Zero dice roll for choice consequences
  const diceResult = Math.floor(Math.random() * 6) + 1;
  const karmaModifier = Math.floor(playerKarma / 10);
  const totalResult = diceResult + karmaModifier;
  
  const karmaChange = totalResult >= 5 ? 5 : totalResult <= 2 ? -5 : 0;
  const stressGained = diceResult === 1 ? 2 : 0;
  
  // Check for triggered encounters
  const triggeredEncounter = scenario.encounters?.find(e => 
    Math.abs(playerKarma + karmaChange - (e.karmaRequirement || 0)) <= 10
  );
  
  return {
    accessGranted: true,
    karmaChange,
    encounterTriggered: triggeredEncounter?.id,
    stressGained
  };
};